import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import {
    WarningOutlined,
    ReloadOutlined,
    CloseOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';

/**
 * CreSyncWarningBanner.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * FEATURE: Omni / CRE Integration Health Banner
 * 
 * Target Audience:
 *   - Only displayed to privileged team members (Admins, Project Managers, BDs)
 *     who perform actions that sync with the CRE client portal.
 *   - Hidden for regular employees to avoid unnecessary confusion.
 * 
 * Behavior:
 *   - Checks GET /api/system/integration-status on mount.
 *   - If CRE is unreachable OR has failed syncs in retry queue:
 *       Renders an informative, reassuring amber banner explaining that
 *       changes are saved safely in ERP and will auto-sync once CRE recovers.
 *   - Includes an instant [Retry Sync Now] action button.
 *   - Self-dismisses once CRE connection returns to healthy.
 * 
 * To revert in the future:
 *   Simply remove <CreSyncWarningBanner /> from ProjectManagement/index.jsx.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const ELEVATED_ROLES = [
    'super_admin',
    'admin',
    'Admin',
    'project manager',
    'projectManager',
    'manager',
    'business_developer',
    'businessDeveloper',
    'BD',
];

export default function CreSyncWarningBanner() {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const [statusData, setStatusData] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [replaying, setReplaying] = useState(false);
    const [replaySuccessMsg, setReplaySuccessMsg] = useState(null);

    const userRole = currentAdmin?.role || '';
    const isElevated = ELEVATED_ROLES.some(
        (r) => r.toLowerCase() === userRole.toLowerCase()
    );

    const checkIntegrationStatus = useCallback(async () => {
        if (!isElevated) return;
        try {
            const res = await axios.get('/system/integration-status');
            if (res.data && res.data.success) {
                setStatusData(res.data);
            }
        } catch (err) {
            // If system endpoint has a temporary hiccup, do not break the UI
            console.debug('[CreSyncWarningBanner] Status check skipped:', err.message);
        }
    }, [isElevated]);

    useEffect(() => {
        checkIntegrationStatus();
        // Periodically refresh status every 2 minutes
        const interval = setInterval(checkIntegrationStatus, 120000);
        return () => clearInterval(interval);
    }, [checkIntegrationStatus]);

    const handleManualReplay = async () => {
        setReplaying(true);
        setReplaySuccessMsg(null);
        try {
            const res = await axios.post('/api/system/health/webhooks/replay-failed');
            if (res.data?.success) {
                setReplaySuccessMsg(`Replayed ${res.data.data?.replayed || 0} event(s). Checking status...`);
                setTimeout(() => {
                    checkIntegrationStatus();
                    setReplaySuccessMsg(null);
                }, 3000);
            }
        } catch (e) {
            console.error('[CreSyncWarningBanner] Replay error:', e.message);
        } finally {
            setReplaying(false);
        }
    };

    // If dismissed by user, or user is not elevated, do not render
    if (dismissed || !isElevated || !statusData) {
        return null;
    }

    const isDegraded =
        !statusData.creHealthy ||
        (statusData.syncSummary?.failed > 0) ||
        (statusData.syncSummary?.currentlyRetrying > 0);

    // If completely healthy, remain invisible
    if (!isDegraded) {
        return null;
    }

    return (
        <div className="mb-4 rounded-xl border border-amber-300 dark:border-amber-700/60 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 p-4 shadow-sm transition-all duration-300">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <WarningOutlined className="text-base" />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-amber-900 dark:text-amber-200">
                                Omni / CRE Client Portal Sync Delayed
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-200 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300">
                                {!statusData.creHealthy ? 'CRE Offline' : 'Retrying Sync'}
                            </span>
                        </div>
                        <p className="text-xs text-amber-800/90 dark:text-amber-300/80 leading-relaxed max-w-3xl">
                            The connection to the CRE client portal is temporarily delayed. All project, milestone,
                            and deliverable updates you make are <strong>saved safely in ERP</strong> and will
                            automatically synchronize with CRE as soon as the connection recovers.
                        </p>
                        {statusData.syncSummary?.failed > 0 && (
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                                Pending sync queue: {statusData.syncSummary.failed} event(s) queued for retry.
                            </p>
                        )}
                        {replaySuccessMsg && (
                            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                                <CheckCircleOutlined /> {replaySuccessMsg}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={handleManualReplay}
                        disabled={replaying}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 hover:bg-amber-700 text-white shadow-xs transition-colors disabled:opacity-50 cursor-pointer"
                        title="Force re-sync of all pending events to CRE"
                    >
                        <ReloadOutlined className={replaying ? 'animate-spin' : ''} />
                        {replaying ? 'Syncing...' : 'Retry Sync'}
                    </button>
                    <button
                        onClick={() => setDismissed(true)}
                        className="p-1.5 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200 rounded-lg hover:bg-amber-200/50 dark:hover:bg-amber-900/40 transition-colors"
                        title="Dismiss notification for this session"
                    >
                        <CloseOutlined className="text-xs" />
                    </button>
                </div>
            </div>
        </div>
    );
}
